import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '../../lib/cn';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../tooltip';

export type CurrencyCode = 'CZK' | 'EUR' | 'USD' | string;

export interface FxSnapshot {
  rate: number;
  source: string;
  capturedAt: string | Date;
  baseCurrency?: string;
}

export interface CurrencyDisplayProps extends React.HTMLAttributes<HTMLSpanElement> {
  amount: number;
  currency: CurrencyCode;
  /** Optional FX snapshot — when present a tooltip is shown. */
  fxSnapshot?: FxSnapshot;
  /** Override BCP-47 locale. */
  locale?: string;
}

function pickLocale(currency: string, fallback?: string): string {
  if (fallback) return fallback;
  if (currency === 'CZK') return 'cs-CZ';
  if (currency === 'EUR') return 'en-IE';
  return 'en-US';
}

export const CurrencyDisplay = React.forwardRef<HTMLSpanElement, CurrencyDisplayProps>(
  ({ className, amount, currency, fxSnapshot, locale, ...props }, ref) => {
    const { i18n } = useTranslation();
    const langDefault =
      i18n?.language && i18n.language.toLowerCase().startsWith('cs')
        ? 'cs-CZ'
        : undefined;
    const resolved = pickLocale(currency, locale ?? langDefault);
    const formatted = React.useMemo(() => {
      try {
        return new Intl.NumberFormat(resolved, {
          style: 'currency',
          currency,
          maximumFractionDigits: 2,
        }).format(amount);
      } catch {
        return `${amount} ${currency}`;
      }
    }, [amount, currency, resolved]);

    const node = (
      <span
        ref={ref}
        className={cn('tabular-nums font-medium', className)}
        {...props}
      >
        {formatted}
      </span>
    );

    if (!fxSnapshot) return node;
    const captured =
      typeof fxSnapshot.capturedAt === 'string'
        ? fxSnapshot.capturedAt
        : fxSnapshot.capturedAt.toISOString();
    const base = fxSnapshot.baseCurrency ?? 'EUR';
    return (
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>{node}</TooltipTrigger>
          <TooltipContent>
            <div className="flex flex-col gap-0.5 text-[11px]">
              <div>
                Rate: 1 {base} = {fxSnapshot.rate} {currency}
              </div>
              <div className="text-[var(--color-fg-muted)]">
                Source: {fxSnapshot.source}
              </div>
              <div className="text-[var(--color-fg-muted)]">Captured: {captured}</div>
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  },
);
CurrencyDisplay.displayName = 'CurrencyDisplay';
